import React from "react";
import "./styleguide-v2-tokens.scss";   // shared .sg-v2 tokens only — decoupled from the
                                        // full styleguide-v2.scss so the palette never breaks
                                        // when that file is mid-edit (recovery 2026-08-07).
import "./palette-explorer.scss";

/**
 * /styleguide-v2/palette — STANDALONE colour exploration.
 *
 * Kept as its own page/route so the main /styleguide-v2 page is never touched.
 * Explores the primary-action colour for a ~95%-male merchant base, where a
 * purple-forward primary can read as too soft — while KEEPING purple as the
 * natural accent. Every candidate stays in purple's family: a deeper, more
 * masculine primary + bright purple returning as the accent (focus ring,
 * active-tab underline, status dot).
 *
 * Everything is scoped under `.pe` (see palette-explorer.scss); the page is
 * wrapped in `sg-v2 pe` so it inherits the v2 surface/type/motion tokens.
 */

const CANDIDATES = [
  {
    key: "graphite",
    tag: "Option A · Graphite (light, grey-first)",
    name: "Graphite",
    // Grey ramp — surfaces + text tiers, NOT colour. Accent stays indigo but is
    // used on very few elements (the one primary button, focus ring, active tab).
    ramp: ["#3f3f46", "#71717a", "#e4e4e7"],
    accent: "#6d5cf0",
    greyFirst: true,
    desc:
      "A light, grey-first theme: near-white surfaces, warm-neutral greys for everything structural, and the indigo accent held back for a SINGLE primary action + focus/active states. Calm, editorial, low-colour — the app reads as a tool, not a brand splash.",
  },
  {
    key: "amber",
    tag: "Option B · Amber Graphite  ★ new",
    name: "Amber Graphite",
    // Graphite primary ramp (same greys as Graphite) + a GOLD accent chip.
    ramp: ["#3f3f46", "#52525b", "#27272a"],
    accent: "#e8a33d",
    gold: true,
    desc:
      "Graphite base — same neutral greys as the Graphite option — with a warm degradé-gold ACCENT (amber → orange-gold). Gold never fills a button; it appears only on the accent moments: status, active tab, badge, focus ring, and the glow pill from your reference. Masculine, premium, watch-world adjacent.",
  },
  {
    key: "e",
    tag: "Option D · Indigo Pro  ★ recommended",
    name: "Indigo Pro",
    ramp: ["#474373", "#544f86", "#37345c"],
    accent: "#7a63f8",
    desc:
      "My pick for the software. Your indigo, tuned a hair richer so buttons keep presence on the dark surfaces instead of greying out — same brand family, masculine, and the active-state clarity you liked.",
    recommended: true,
  },
];

// The IDENTICAL component set both themes render — the whole point is apple-to-
// apple, so dark and light must show exactly the same widgets. One definition,
// used by both sides, so they can never drift.
const CardBody = ({ c }) => (
  <>
    {/* swatch strip (same on both) */}
    <div className="pe__swatch">
      <span style={{ background: c.ramp[0] }} />
      <span style={{ background: c.ramp[1] }} />
      <span style={{ background: c.ramp[2] }} />
      <span className="pe-accent" style={{ background: c.accent }} />
    </div>
    <div className="pe__hexes">
      <span>{c.ramp[0]} primary</span>
      <span>{c.accent} accent</span>
    </div>

    <p className="pe__desc">{c.desc}</p>

    {/* Controls grouped so EACH CATEGORY is its own horizontal line — read across
        a row to compare like-with-like (all buttons / all tabs / all toggles). */}
    <div className="pe__controls">
      {/* Line 1 — Tabs (the top menu nav) */}
      <div className="pe__line">
        <button className="pe__tab is-active">Listings</button>
        <button className="pe__tab">Orders</button>
        <button className="pe__tab">Leads</button>
      </div>

      {/* Line 2 — Action buttons: Save / Cancel / Discard, all on ONE line */}
      <div className="pe__line pe__line--btns">
        <button className="pe__btn pe__btn--primary">Save</button>
        <button className="pe__btn pe__btn--secondary">Cancel</button>
        <button className="pe__btn pe__btn--ghost">Discard</button>
      </div>

      {/* Line 3 — Disabled state on its own line below */}
      <div className="pe__line">
        <button className="pe__btn pe__btn--primary" disabled>Disabled</button>
      </div>

      {/* Line 4 — Input */}
      <div className="pe__line">
        <input className="pe__input" defaultValue="126334" aria-label="Reference number" />
      </div>

      {/* Line 5 — Toggles: On (green) · On (neutral) · Off */}
      <div className="pe__line">
        <span className="pe__switch-row">
          <button className="pe__switch is-on" aria-pressed="true" /> On
        </span>
        <span className="pe__switch-row">
          <button className="pe__switch is-on pe__switch--neutral" aria-pressed="true" /> On (neutral)
        </span>
        <span className="pe__switch-row">
          <button className="pe__switch" aria-pressed="false" /> Off
        </span>
      </div>

      {/* Line 6 — Badges */}
      <div className="pe__line">
        <span className="pe__badge">Live</span>
        <span className="pe__badge">New</span>
      </div>
    </div>

    {/* Loading pill — LAST element in the card (bottom), glowing in this accent */}
    <div className="pe__loadrow">
      <span className="pe__load-pill"><span className="pe__load-spin" /> 10%</span>
    </div>
  </>
);

// One card, rendered in either theme. `light` only adds the .pe__light wrapper
// class — the CONTENT is identical, guaranteeing the comparison is fair.
const ThemeCard = ({ c, light }) => (
  <div
    className={`pe__card pe__card--${c.key}${light ? " pe__light" : ""}${
      c.recommended ? " pe__card--rec" : ""
    }`}
  >
    <span className="pe__tag">{light ? "Light theme" : "Dark theme"}</span>
    <p className="pe__name">{c.name}</p>
    <CardBody c={c} />
  </div>
);

// One colour, both themes side by side — identical components, dark left / light
// right — so each option is a true apple-to-apple comparison across themes.
const ThemePair = ({ c }) => (
  <div className="pe__pair">
    <p className="pe__pair-title">
      {c.name}
      {c.recommended && <span className="pe__pair-rec">★ recommended</span>}
    </p>
    <div className="pe__pair-row">
      <div className="pe__stage pe__stage--dark"><ThemeCard c={c} /></div>
      <div className="pe__stage pe__stage--light"><ThemeCard c={c} light /></div>
    </div>
  </div>
);

const PaletteExplorerPage = () => (
  <div className="sg-v2 pe">
    <div className="pe__wrap">
      <a className="pe__back" href="/styleguide-v2">← Back to Design System v2</a>
      <p className="pe__eyebrow">Retail Circle · Software</p>
      <h1 className="pe__title">Primary colour — options</h1>
      <p className="pe__lead">
        The merchant base is ~95% men, and purple filling every button can read
        as too soft. But purple is our natural accent — so instead of replacing
        it, each option below keeps purple in the <strong>family</strong>: the
        primary becomes a deeper, more masculine member of the purple range, and
        purple returns <strong>bright, as the accent</strong> (focus ring,
        active-tab underline, status dot). Surfaces, type and motion are
        unchanged; only the action colour moves.
      </p>

      <h3 className="pe__h3">For reference — today's purple-forward primary</h3>
      <div className="pe__ref-row">
        <button className="pe__ref-btn pe__ref-btn--primary">Primary action</button>
        <button className="pe__ref-btn pe__ref-btn--secondary">Secondary</button>
        <button className="pe__ref-btn pe__ref-btn--ghost">Ghost</button>
      </div>
      <p className="pe__caption">
        The gradient purple fill — the loudest thing on the screen, and what
        reads as too soft when it's on every button.
      </p>

      <h3 className="pe__h3">Each option — dark &amp; light theme, side by side</h3>
      <p className="pe__caption" style={{ marginTop: 0, marginBottom: "var(--v2-s5)" }}>
        Left is the dark theme, right is the light theme of the <em>same</em>{" "}
        colour, so each option can be judged across both. The light theme{" "}
        <strong>keeps the same indigo primary and purple accent</strong> as dark
        (no black buttons) and sits them on <strong>frosted glass over soft
        grey</strong> — depth from translucency and grey shades, not colour.
      </p>
      <div className="pe__pairs">
        {CANDIDATES.map((c) => <ThemePair key={c.key} c={c} />)}
      </div>
    </div>
  </div>
);

export default PaletteExplorerPage;
